import React, { useState, useEffect } from "react";
import { Monitor, Eye, EyeOff, Settings, Save, RefreshCw } from "lucide-react";
import { useJudgeStationSettings } from "../../../hooks/useJudgeStationSettings";

/**
 * JudgeStationSettingsTab
 * 
 * Tab component for managing judge station specific preferences and settings
 * Integrates with the existing JudgingAwardsSettings page
 */
export function JudgeStationSettingsTab() {
  const {
    settings,
    loading,
    error,
    hasChanges,
    successMessage,
    updateSetting,
    saveJudgeStationSettings,
    resetSettings
  } = useJudgeStationSettings();

  const [localSettings, setLocalSettings] = useState(settings);

  // Update local settings when hook settings change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Track local changes
  const handleSettingChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    updateSetting(key, value);
  };

  const handleSave = async () => {
    await saveJudgeStationSettings();
  };

  const handleReset = () => {
    resetSettings();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-base-content flex items-center gap-2">
            <Monitor className="w-6 h-6 text-primary" />
            Judge Station Settings
          </h3>
          <p className="text-base-content/60 mt-1">
            Configure how judge stations behave and what information is visible to judges
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            className="btn btn-outline btn-sm"
            onClick={handleReset}
            disabled={loading || !hasChanges}
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
          <button
            className={`btn btn-primary btn-sm ${loading ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            {loading ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="alert alert-error shadow-lg">
          <div>
            <span>{error}</span>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success shadow-lg">
          <div>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Settings Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Livescore Monitor Settings */}
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body">
            <h4 className="card-title text-lg flex items-center gap-2">
              {localSettings.livescoreMonitorEnabled ? (
                <Eye className="w-5 h-5 text-success" />
              ) : (
                <EyeOff className="w-5 h-5 text-error" />
              )}
              Livescore Monitor
            </h4>
            
            <div className="space-y-4">
              {/* Enable/Disable Livescore Monitor */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text font-medium">
                    Enable Livescore Monitor for Judges
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={localSettings.livescoreMonitorEnabled}
                    onChange={(e) => handleSettingChange('livescoreMonitorEnabled', e.target.checked)}
                  />
                </label>
                <div className="label">
                  <span className="label-text-alt text-base-content/60">
                    When enabled, judges can see live scores during competitions. 
                    Some conventions prefer to disable this to prevent bias.
                  </span>
                </div>
              </div>

              {/* Show Other Judge Scores */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text font-medium">
                    Show Individual Judge Scores
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-info"
                    checked={localSettings.showOtherJudgeScores}
                    onChange={(e) => handleSettingChange('showOtherJudgeScores', e.target.checked)}
                    disabled={!localSettings.livescoreMonitorEnabled}
                  />
                </label>
                <div className="label">
                  <span className="label-text-alt text-base-content/60">
                    When the livescore monitor is enabled, this controls whether judges 
                    can see individual scores from other judges or only totals.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Judge Station Behavior */}
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body">
            <h4 className="card-title text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-warning" />
              Station Behavior
            </h4>
            
            <div className="space-y-4">
              {/* Allow Judge Navigation */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text font-medium">
                    Allow Judge Navigation
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-warning"
                    checked={localSettings.allowJudgeNavigation}
                    onChange={(e) => handleSettingChange('allowJudgeNavigation', e.target.checked)}
                  />
                </label>
                <div className="label">
                  <span className="label-text-alt text-base-content/60">
                    Allow judges to navigate between routines independently. 
                    When disabled, judges can only view the current routine set by the tabulator.
                  </span>
                </div>
              </div>

              {/* Enable Score Comments */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text font-medium">
                    Enable Score Comments
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-secondary"
                    checked={localSettings.enableScoreComments}
                    onChange={(e) => handleSettingChange('enableScoreComments', e.target.checked)}
                  />
                </label>
                <div className="label">
                  <span className="label-text-alt text-base-content/60">
                    Allow judges to add comments to their scores. 
                    Comments can be helpful for feedback but may slow down judging.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Information Card */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body">
          <h4 className="card-title text-lg">Important Notes</h4>
          <div className="space-y-3 text-sm text-base-content/80">
            <p>
              <strong>Livescore Monitor:</strong> When disabled, judges will not see the live scoring 
              panel in their judge station interface. This can help prevent bias but may reduce 
              transparency.
            </p>
            <p>
              <strong>Score Visibility:</strong> Even when the livescore monitor is enabled, you can 
              choose to hide individual judge scores and only show totals or averages.
            </p>
            <p>
              <strong>Changes Take Effect:</strong> Settings changes will apply to all new judge 
              station sessions. Existing sessions may need to be refreshed to see the changes.
            </p>
            <p>
              <strong>Data Storage:</strong> These settings are stored securely in AWS and are 
              shared across all competitions for your organization.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
